"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.langchainProvider = void 0;
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
/**
 * LangChain / LangGraph convention: this module owns the `lc`/`kwargs`
 * serialization envelope and the `tool_calls`/`invalid_tool_calls`/
 * `additional_kwargs` sibling fields. LangChain has no finish-reason
 * vocabulary of its own — it surfaces the underlying provider's value under
 * `response_metadata` (picked up generically in `normalize/message.ts`).
 */
// FunctionMessage maps to the deprecated "function" role so the legacy
// function-result handling (name as tool name) applies.
const LANGCHAIN_ROLE_BY_CLASS = {
    SystemMessage: "system",
    HumanMessage: "user",
    AIMessage: "assistant",
    AIMessageChunk: "assistant",
    ToolMessage: "tool",
    FunctionMessage: "function",
};
/**
 * The LangChain `lc`/`kwargs` serialization envelope: the message fields
 * live in `kwargs`, and the role derives from the class path in `id`.
 */
function unwrapLangchainEnvelope(value, fallbackRole, ctx) {
    const kwargs = value.lc !== undefined ? (0, json_1.asRecord)(value.kwargs) : undefined;
    if (!kwargs)
        return __1.unmatched;
    const classPath = Array.isArray(value.id) ? value.id : [];
    const className = (0, json_1.optionalString)(classPath[classPath.length - 1]);
    const role = className ? LANGCHAIN_ROLE_BY_CLASS[className] : undefined;
    return (0, __1.claimed)(ctx.normalizeMessage({ ...(role ? { role } : {}), ...kwargs }, fallbackRole));
}
/**
 * LangChain's `invalid_tool_calls` (attempts the model made whose arguments
 * could not be parsed — kept in the stream as flagged tool calls, raw args
 * as input, so evals can filter them; excluded from the tool columns) and
 * `additional_kwargs.tool_calls` (an echo of the same `tool_calls` shape).
 */
function langchainCollectSiblingParts(value, _baseParts, context) {
    const parts = [];
    const additionalKwargs = (0, json_1.asRecord)(value.additional_kwargs);
    const additionalToolCalls = context.normalizePartList((0, json_1.parseArray)(additionalKwargs?.tool_calls) ?? []);
    parts.push(...additionalToolCalls);
    for (const invalidCall of (0, json_1.parseArray)(value.invalid_tool_calls) ?? []) {
        // LangChain invalid_tool_calls entries: { name, args, id, error }.
        const record = (0, json_1.asRecord)(invalidCall);
        const part = record
            ? (0, tool_calls_1.toolCallPart)({
                toolCallId: record.id,
                toolName: record.name,
                input: record.args,
            })
            : null;
        if (!part)
            continue;
        const error = (0, json_1.optionalString)(record?.error);
        parts.push((0, json_1.compact)({
            ...part,
            invalid: true,
            providerMetadata: error ? { error } : undefined,
        }));
    }
    return parts.length > 0
        ? [{ sourceKey: "langchain.siblings", slot: "after-tool-calls", parts }]
        : [];
}
function langchainMessages(root, kind) {
    if (kind !== "output" || !Array.isArray(root.generations))
        return [];
    // LLMResult batches generations; ChatResult contains a flat list.
    const sources = [];
    for (const value of root.generations.flat(1)) {
        const generation = (0, json_1.asRecord)(value);
        if (!generation)
            return [];
        const message = (0, json_1.asRecord)(generation.message);
        if (!message && typeof generation.text !== "string")
            return [];
        sources.push({
            kind: "single",
            value: message ?? generation.text,
            fallbackRole: "assistant",
            finishReasonCarrier: (0, json_1.asRecord)(generation.generation_info),
        });
    }
    return sources;
}
exports.langchainProvider = {
    name: "langchain",
    claimMessages: langchainMessages,
    // Serialized LangChain message classes carry their role as a type string.
    roleByMessageType: {
        human: "user",
        ai: "assistant",
        tool: "tool",
        system: "system",
    },
    // Parsed calls live in `tool_calls`, raw provider extras in
    // `additional_kwargs`.
    messageLikeKeys: new Set(["tool_calls", "additional_kwargs"]),
    tryUnwrapMessage: unwrapLangchainEnvelope,
    collectSiblingParts: langchainCollectSiblingParts,
};
